import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Maximize2, Pause, Pencil, Plus, Trash2, Video, X } from "lucide-react";

import { apiDelete, apiGet, apiPatch, apiPost } from "../../Api/Client.js";
import { safeHttpUrl } from "../../Api/safeHttpUrl.js";
import { CopyButton } from "../../Shared/CopyButton.jsx";
import { TaskIdChip } from "../../Shared/TaskIdChip.jsx";
import { useTaskDescription } from "../../Shared/TaskDescription.jsx";
import { TaskGitLinks, useTaskGitLinks } from "../../Shared/TaskGitLinks.jsx";
import { ContributorChips, Fold, IntegrationHealthBanner, ownerLabelsForTeam, SprintProgress, TeamIdentity } from "../../Shared/ScreenComponents.jsx";
import { formatDateTime, isoDate } from "../../Shared/ScreenUtils.jsx";
import "../../Styles/ProjectSanity.css";

/* ─── Projects — the executor view ────────────────────────────────────────
   Two levels on one page, on purpose.

   The list is your slice: only work assigned to you, because that is what
   somebody opening this page in the morning actually needs. Everything else
   about the project is one click away rather than crowded in beside it.

   Expanding a card opens the whole project board over the page — every task,
   whoever owns it — without navigating anywhere. Same page, same scroll
   position underneath, Escape or the close button puts it back.

   Documents and meeting intelligence are inert slots. Do not remove them when
   those get built; fill them.
   ────────────────────────────────────────────────────────────────────────── */

function dueLabel(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function isLate(task) {
  return !task.is_done && task.due_at && new Date(task.due_at) < new Date();
}

/* Meeting start times default to "now", rounded up to the next 5-minute
   mark — never a slot that's already in the past, and always landing on a
   mark the 5-minute time-picker step can actually show. */
function roundUpToFiveMinutes(value) {
  const result = new Date(value);
  result.setSeconds(0, 0);
  const remainder = result.getMinutes() % 5;
  if (remainder !== 0) result.setMinutes(result.getMinutes() + (5 - remainder));
  return result;
}

function timeInputValue(value) {
  return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
}

// A plain dropdown instead of a native time input — the native picker's
// seconds spinner can't be turned off reliably across browsers even with a
// 5-minute `step`, and a dropdown can only ever offer the slots in this list.
const TIME_SLOTS = Array.from({ length: 24 * 12 }, (_, i) => {
  const totalMinutes = i * 5;
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
});

function timeSlotLabel(value) {
  const [h, m] = value.split(":").map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

const DURATION_OPTIONS = [15, 30, 45, 60];


/* One task row. `onToggle` and `onDelete` are optional — where they're absent
   the row is simply read-only. `ownerLabel` is the name to show for the
   owner — first name alone, unless someone else on this same project shares
   that first name, in which case the caller resolves it to the full name
   instead (see ownerLabelsForTeam below). Never initials — that was the ask. */
function TaskRow({ task, onToggle, onDelete, showOwner, ownerLabel, gitLinks }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const desc = useTaskDescription(task);

  // A refused tick or delete says so on the row; the list is only refreshed when it worked.
  const run = async (fn) => {
    setBusy(true);
    setFailure("");
    try {
      await fn();
    } catch (err) {
      setFailure(err?.payload?.detail || err?.message || "That did not go through. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <div className={"Exec-Task" + (showOwner ? " with-owner" : "") + (task.is_done ? " done" : "")}>
      {onToggle ? (
        <button
          type="button"
          className={"Task-Tick" + (task.is_done ? " on" : "")}
          disabled={busy}
          title={task.is_done ? "Mark as not done" : "Mark as done"}
          aria-label={task.is_done ? "Mark as not done" : "Mark as done"}
          onClick={() => run(() => onToggle(task))}
        >
          {task.is_done && <Check size={12} strokeWidth={3} />}
        </button>
      ) : (
        <span className={"Task-Tick static" + (task.is_done ? " on" : "")}>
          {task.is_done && <Check size={12} strokeWidth={3} />}
        </span>
      )}

      <span className={"Exec-Task-Title" + (task.is_done ? " done" : "")}><TaskIdChip id={task.id} />{desc.pill}{task.title}</span>

      {showOwner && (
        <span className="Task-Owner" title={task.owner || ""}>{task.owner ? (ownerLabel || task.owner) : ""}</span>
      )}
      {/* Points/late/due always render, blank when they don't apply — the
          same "reserve the slot, don't collapse it" rule already used for
          the sprint-late pill just below. A task with no points and one
          with 12 used to put everything after them (owner, due date) in a
          different horizontal spot row to row; a slot that's just there and
          empty can't do that. */}
      <span className="Exec-Task-Points">{task.points > 0 ? `${task.points} pts` : ""}</span>
      <span className="Exec-Task-Pill late">{isLate(task) ? "Late" : ""}</span>
      <span className="Task-Due">{task.due_at ? dueLabel(task.due_at) : ""}</span>

      {onDelete && (
        <button
          type="button"
          className="Task-Del"
          disabled={busy}
          title="Delete this task"
          aria-label="Delete this task"
          onClick={() => { if (window.confirm(`Delete "${task.title}"?`)) run(() => onDelete(task)); }}
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
    {failure && <p className="Team-Remove-Error" role="alert">{failure}</p>}
    {desc.panel}
    <TaskGitLinks links={gitLinks} />
    </>
  );
}

/* Add-a-task form. Collapsed to a single button until wanted, so the common
   case (reading your list) isn't buried under a form nobody asked for. */
function AddTask({ projectId, sprints, team, open: openProp, onClose, onAdded }) {
  // Two ways to use it: on its own (the project card: a button that opens the
  // form), or driven by a trigger elsewhere (the pop-up's compact toolbar passes
  // `open` and `onClose`, and renders no button of its own).
  const controlled = openProp !== undefined;
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlled ? openProp : innerOpen;
  const setOpen = (value) => { if (controlled) { if (!value) onClose(); } else setInnerOpen(value); };
  const [title, setTitle] = useState("");
  const [points, setPoints] = useState("");
  const [sprint, setSprint] = useState("");
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setTitle(""); setPoints(""); setSprint(""); setOwner(""); setError(""); };

  const submit = async (event) => {
    event.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    setError("");
    try {
      await apiPost("/Project/tasks/", {
        project: projectId,
        title: title.trim(),
        points: Number(points) || 0,
        sprint: sprint || null,
        owner: owner || null,
      });
      reset();
      setOpen(false);
      onAdded();
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Could not add that task.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    if (controlled) return null;
    return (
      <button type="button" className="Add-Task-Btn" onClick={() => setOpen(true)}>
        <Plus size={14} /> Add a task
      </button>
    );
  }

  return (
    <form className="Add-Task-Form" onSubmit={submit}>
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What needs doing?"
        className="Add-Task-Title"
      />
      <input
        value={points}
        onChange={(e) => setPoints(e.target.value)}
        placeholder="Points"
        inputMode="numeric"
        className="Add-Task-Points"
      />
      {sprints.length > 0 && (
        <select value={sprint} onChange={(e) => setSprint(e.target.value)}>
          <option value="">No sprint</option>
          {sprints.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}
      {team.length > 0 && (
        <select value={owner} onChange={(e) => setOwner(e.target.value)}>
          <option value="">Me</option>
          {team.map((m) => <option key={m.employee_id} value={m.employee_id}>{m.display_name}</option>)}
        </select>
      )}
      <button type="submit" className="Primary-Button Small" disabled={busy || !title.trim()}>
        {busy ? "Adding…" : "Add"}
      </button>
      <button type="button" className="Soft-Button Small" onClick={() => { reset(); setOpen(false); }}>Cancel</button>
      {error && <div className="error-banner Add-Task-Error">{error}</div>}
    </form>
  );
}

const RECURRENCE_OPTIONS = [
  ["", "Doesn't repeat"],
  ["daily", "Daily"],
  ["weekly", "Weekly"],
  ["custom", "Custom"],
];

// Google's own day-initial convention (S M T W T F S) — repeated letters are
// fine there because the circles carry the real signal, not the letter.
const DAY_CODES = [
  ["SU", "S"], ["MO", "M"], ["TU", "T"], ["WE", "W"], ["TH", "T"], ["FR", "F"], ["SA", "S"],
];

// Deliberately no "ends" sub-option (never / on date / after N) the way
// Google's own custom-recurrence panel has one — asked to leave it out to
// keep this to the one thing it's actually for: which days, not when it
// eventually stops.
function CustomDayPicker({ days, onToggle }) {
  return (
    <div className="Schedule-Meeting-Days">
      {DAY_CODES.map(([code, letter]) => (
        <button
          key={code}
          type="button"
          className={"Schedule-Meeting-Day" + (days.includes(code) ? " on" : "")}
          onClick={() => onToggle(code)}
          aria-pressed={days.includes(code)}
          title={code}
        >
          {letter}
        </button>
      ))}
    </div>
  );
}

// Search-and-checklist over the whole company, not just this project's
// team — the team is only where the checkboxes start, not a ceiling on who
// can be invited. "Select all" means the whole candidate list currently in
// view (post-search), matching how that phrase reads everywhere else.
//
// The panel is portaled to <body> and positioned from the trigger's real
// screen coordinates, not laid out inline under it. This form and the
// meeting rows that reuse it both live inside the project board, a modal
// that clips its own overflow to keep its rounded corners clean — a plain
// `position: absolute` panel got silently cut off (or rendered detached,
// depending on scroll position) any time the row it opened from wasn't
// near the very top of the board. Portaling escapes that clip entirely,
// the same fix already used for the LMS's row context menu and note popover.
function AttendeePicker({ allEmployees, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pinnedIds, setPinnedIds] = useState([]);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = q ? allEmployees.filter((e) => e.display_name.toLowerCase().includes(q)) : allEmployees;
    // Whoever was already invited when the panel was opened stays pinned at
    // the top for the rest of this editing session — who's already coming
    // is the first thing to check, and ticking a box mid-session must not
    // reshuffle the list out from under the pointer.
    const pinned = pool.filter((e) => pinnedSet.has(e.id));
    const rest = pool.filter((e) => !pinnedSet.has(e.id));
    return [...pinned, ...rest].slice(0, 60);
  }, [allEmployees, query, pinnedSet]);

  const toggle = (id) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };
  const selectAllVisible = () => {
    const ids = new Set(selectedIds);
    filtered.forEach((e) => ids.add(e.id));
    onChange([...ids]);
  };
  const clearAllVisible = () => {
    const visible = new Set(filtered.map((e) => e.id));
    onChange(selectedIds.filter((id) => !visible.has(id)));
  };
  const allVisibleSelected = filtered.length > 0 && filtered.every((e) => selectedIds.includes(e.id));

  const closePanel = () => setOpen(false);
  const openPanel = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      // Width and an estimated max height (search + select-all + the
      // 220px-capped list + Done, plus the gaps between them) — the real
      // height varies with how many rows fit, but this only needs to be a
      // safe upper bound for the below-vs-above decision, not exact.
      const width = 260;
      const estimatedHeight = 340;
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      const spaceBelow = window.innerHeight - rect.bottom;
      // Prefer opening downward unless there's markedly more room above —
      // and always clamp the result into the viewport afterward, so even a
      // trigger sitting right at (or, worst case, past) the screen edge
      // still lands the panel somewhere fully visible rather than trusting
      // the unclamped math to keep it on-screen.
      const openDownward = spaceBelow >= estimatedHeight || spaceBelow >= rect.top;
      const rawTop = openDownward ? rect.bottom + 6 : rect.top - estimatedHeight - 6;
      // Clamp against the panel's own estimated height, not just against 0 —
      // capping `top` alone still lets a tall panel run off the bottom edge
      // when there simply isn't estimatedHeight of room anywhere on screen.
      const top = Math.max(8, Math.min(rawTop, window.innerHeight - estimatedHeight - 8));
      setCoords({ left, top });
    }
    setPinnedIds(selectedIds);
    setQuery("");
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => { if (!panelRef.current?.contains(e.target)) closePanel(); };
    const onKey = (e) => { if (e.key === "Escape") closePanel(); };
    // Scrolling an ANCESTOR — the board, the page — invalidates the
    // computed position, so closing is correct there. But `scroll` doesn't
    // bubble, so a capture-phase window listener is the only way to hear
    // ancestor scrolls at all, and that same listener also fires for the
    // attendee list's own internal scroll (.Attendee-List), since that's
    // a descendant too. Without this check, scrolling the list itself
    // closed the panel before the scroll could register — nobody could
    // reach anyone past row nine. Only close for scrolls that didn't
    // originate inside the panel.
    const onScroll = (e) => { if (!panelRef.current?.contains(e.target)) closePanel(); };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  return (
    <div className="Schedule-Meeting-Attendees">
      <button type="button" ref={triggerRef} className="Attendee-Trigger" onClick={() => (open ? closePanel() : openPanel())}>
        {selectedIds.length} team invited <ChevronDown size={12} className={open ? "rot" : ""} />
      </button>
      {open && coords && createPortal(
        <div className="Attendee-Panel" ref={panelRef} style={coords} onMouseDown={(e) => e.stopPropagation()}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search anyone…"
            className="Attendee-Search"
          />
          <button type="button" className="Attendee-Select-All" onClick={allVisibleSelected ? clearAllVisible : selectAllVisible}>
            {allVisibleSelected ? "Clear all" : "Select all"}
          </button>
          <div className="Attendee-List">
            {filtered.length
              ? filtered.map((e) => (
                  <label key={e.id} className="Attendee-Option">
                    <input type="checkbox" checked={selectedIds.includes(e.id)} onChange={() => toggle(e.id)} />
                    {e.display_name}
                  </label>
                ))
              : <p className="Sanity-Empty">Nobody matches.</p>}
          </div>
          <button type="button" className="Soft-Button Small" onClick={closePanel}>Done</button>
        </div>,
        document.body,
      )}
    </div>
  );
}

// Schedules straight onto the project's own team by default — nobody has to
// pick, because that's the point of scheduling from here instead of a blank
// calendar — but the attendee list is still the whole company underneath
// that default, for the one-on-one or subset case. Same collapsed-until-
// wanted shape as Add-a-task, so the two "create something" affordances on
// this page behave identically instead of each teaching its own
// interaction.
function ScheduleMeetingForm({ projectId, team, allEmployees, onScheduled }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(() => isoDate(new Date()));
  const [start, setStart] = useState("09:30");
  const [duration, setDuration] = useState(30);
  const [recurrence, setRecurrence] = useState("");
  const [days, setDays] = useState([]);
  const [attendeeIds, setAttendeeIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const teamIds = useMemo(() => (team || []).map((m) => m.employee_id), [team]);

  // Fresh "now, rounded up to the next 5 minutes" every time the form is
  // opened for a new meeting — not just once at mount, since the board
  // around this form now stays alive for the whole session (see ProjectBoard).
  useEffect(() => {
    if (open) {
      const nextSlot = roundUpToFiveMinutes(new Date());
      setDate(isoDate(nextSlot));
      setStart(timeInputValue(nextSlot));
      setDuration(30);
      setAttendeeIds(teamIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const endDate = useMemo(() => {
    const parsed = new Date(`${date}T${start}`);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getTime() + duration * 60000);
  }, [date, start, duration]);

  const reset = () => {
    setTitle(""); setDescription(""); setRecurrence(""); setDays([]); setError("");
  };

  const toggleDay = (code) => {
    setDays((current) => current.includes(code) ? current.filter((d) => d !== code) : [...current, code]);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (recurrence === "custom" && days.length === 0) {
      setError("Pick at least one day for a custom repeat.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await apiPost(`/Project/board/${projectId}/meetings/`, {
        title: title.trim() || undefined,
        description: description.trim(),
        starts_at: new Date(`${date}T${start}`).toISOString(),
        ends_at: endDate.toISOString(),
        recurrence: recurrence || undefined,
        recurrence_days: recurrence === "custom" ? days : undefined,
        attendee_ids: attendeeIds,
        sync_provider: "google",
      });
      reset();
      setOpen(false);
      onScheduled();
    } catch (err) {
      setError(err?.payload?.detail || err?.payload?.ends_at || err?.payload?.recurrence || err?.message || "Could not schedule this meeting.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="Add-Task-Btn" onClick={() => setOpen(true)}>
        <Plus size={14} /> Schedule a meeting
      </button>
    );
  }

  return (
    <form className="Schedule-Meeting-Form" onSubmit={submit}>
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Standup"
        className="Schedule-Meeting-Title"
      />
      <div className="Schedule-Meeting-Row">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <select value={start} onChange={(e) => setStart(e.target.value)}>
          {TIME_SLOTS.map((t) => <option key={t} value={t}>{timeSlotLabel(t)}</option>)}
        </select>
        <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
          {DURATION_OPTIONS.map((mins) => <option key={mins} value={mins}>{mins} min</option>)}
        </select>
        <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
          {RECURRENCE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        {recurrence === "custom" && <CustomDayPicker days={days} onToggle={toggleDay} />}
        <AttendeePicker allEmployees={allEmployees} selectedIds={attendeeIds} onChange={setAttendeeIds} />
      </div>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        className="Schedule-Meeting-Description"
      />
      <div className="Schedule-Meeting-Foot">
        <button type="submit" className="Primary-Button Small" disabled={busy}>
          {busy ? "Scheduling…" : "Schedule"}
        </button>
        <button type="button" className="Soft-Button Small" onClick={() => { reset(); setOpen(false); }}>Cancel</button>
      </div>
      {error && <div className="error-banner Add-Task-Error">{error}</div>}
    </form>
  );
}

function SprintStrip({ sprints, team }) {
  if (!sprints.length) return null;
  return (
    <div className="Exec-Releases">
      {sprints.map((s) => (
        <div key={s.id} className={"Exec-Release" + (s.is_paused ? " paused" : "")}>
          <span className="Exec-Release-Name" title={s.name}>{s.name}</span>
          {/* Always rendered, even when there's nothing to say — an omitted
             element shifts this row's later columns into the late-pill's
             grid track instead of their own, which is what was actually
             throwing the bar/points/tasks onto their own line below. Paused
             takes over this same slot rather than adding a second one, same
             "one glance, one signal" reasoning as everywhere else this
             pattern is used — grayed by the row's own .paused filter below,
             same as a red delay pill fades on a paused project's card. */}
          <span className="Sprint-Late">{s.is_paused ? "Paused" : s.days_late > 0 ? `${s.days_late}d late` : ""}</span>
          <SprintProgress sprint={s} />
          <ContributorChips contributors={s.contributors} team={team} />
        </div>
      ))}
    </div>
  );
}

/* One scheduled meeting. Its own component, not inline in a .map — each row
   needs its own title-edit and attendee-edit state, and a single flag living
   in the parent would put every meeting into edit mode together the moment
   one was clicked. Keying by meeting.id already isolates React's re-renders;
   giving each row its own component isolates the actual UI state the same
   way. Layout is two columns: title + who's invited on the left (what you'd
   check), date/time + Join and who owns it on the right (what you'd act on),
   both edit icons appearing only on hovering this specific card. */
function MeetingRow({ meeting, allEmployees, teamWord, onUpdate, onCancel }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(meeting.title);
  const [editingAttendees, setEditingAttendees] = useState(false);

  useEffect(() => { setTitleDraft(meeting.title); }, [meeting.title]);

  const saveTitle = () => {
    const trimmed = titleDraft.trim();
    setEditingTitle(false);
    if (!trimmed || trimmed === meeting.title) { setTitleDraft(meeting.title); return; }
    onUpdate(meeting.id, { title: trimmed });
  };

  return (
    <div className="Exec-Meeting">
      <div className="Exec-Meeting-Top">
        <div className="Exec-Meeting-Title-Group">
          {editingTitle ? (
            <input
              autoFocus
              className="Exec-Meeting-Title-Input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") { setTitleDraft(meeting.title); setEditingTitle(false); }
              }}
            />
          ) : (
            <>
              <span className="name">{meeting.title}{meeting.recurring && <span className="repeats" title={meeting.recurrence_label || "Repeats"}>↻</span>}</span>
              <button type="button" className="Exec-Meeting-Edit-Icon" title="Edit title" aria-label="Edit meeting title" onClick={() => setEditingTitle(true)}>
                <Pencil size={11} />
              </button>
            </>
          )}
        </div>
        <div className="Exec-Meeting-When-Join">
          <span className="when">{formatDateTime(meeting.starts_at)}</span>
          {safeHttpUrl(meeting.meeting_url) && (
            <a href={safeHttpUrl(meeting.meeting_url)} target="_blank" rel="noreferrer">
              <Video size={13} /> Join
            </a>
          )}
        </div>
      </div>

      {meeting.description && <p className="Exec-Meeting-Desc">{meeting.description}</p>}

      <div className="Exec-Meeting-Bottom">
        <div className="Exec-Meeting-Invited-Group">
          {editingAttendees ? (
            <AttendeePicker
              allEmployees={allEmployees}
              selectedIds={meeting.attendee_ids}
              onChange={(ids) => onUpdate(meeting.id, { attendee_ids: ids })}
            />
          ) : (
            <>
              <span>{meeting.attendee_count} {teamWord} invited</span>
              <button type="button" className="Exec-Meeting-Edit-Icon" title="Edit attendees" aria-label="Edit meeting attendees" onClick={() => setEditingAttendees(true)}>
                <Pencil size={11} />
              </button>
            </>
          )}
        </div>
        {meeting.is_mine ? (
          <button type="button" className="Exec-Meeting-Cancel" onClick={() => onCancel(meeting.id)}>Cancel</button>
        ) : (
          meeting.owner && <span className="Exec-Meeting-Setup-By">set up by {meeting.owner}</span>
        )}
      </div>

      {meeting.sync_error && <p className="Exec-Meeting-Warn">{meeting.sync_error}</p>}
    </div>
  );
}

/* The whole project, over the page. Not a route change — the list stays mounted
   underneath, so closing this returns you exactly where you were.

   `active` is whether THIS board is the one currently on screen. A board
   stays mounted (hidden, not destroyed) after it's closed, so a half-typed
   task, an open Team fold, or an in-progress meeting form survives clicking
   outside — the whole point Bassi asked for was "don't lose my work until I
   refresh the page." Escape and the scroll-lock only apply while active, or
   every hidden board left open in the background would fight over them. */
const TASK_PAGE = 40;

function ProjectBoard({ projectId, allEmployees, onClose, onDirty, active }) {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  // Tasks are fetched a page at a time and filtered on the server: a project's
  // task list only ever grows, so the board must not depend on holding all of it.
  const [scope, setScope] = useState("open");
  const [sprintFilter, setSprintFilter] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [list, setList] = useState({ rows: [], count: 0, counts: null, hasMore: false, loading: true });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  // Only the newest request may write to the screen: a slow "Open" answer arriving after a
  // newer "Everything" one must not overwrite it.
  const latestTasksRequest = useRef(0);
  const fetchTasks = useCallback((offset, limit, append) => {
    const qs = new URLSearchParams({ scope, offset: String(offset), limit: String(limit) });
    if (sprintFilter) qs.set("sprint", sprintFilter);
    if (debouncedQuery) qs.set("q", debouncedQuery);
    latestTasksRequest.current += 1;
    const requestId = latestTasksRequest.current;
    return apiGet(`/Project/board/${projectId}/tasks/?${qs.toString()}`)
      .then((res) => {
        if (requestId !== latestTasksRequest.current) return;
        setList((prev) => ({
          rows: append ? [...prev.rows, ...res.results] : res.results,
          count: res.count,
          counts: res.counts,
          hasMore: res.has_more,
          loading: false,
        }));
      })
      .catch((e) => {
        if (requestId !== latestTasksRequest.current) return;
        setError(e?.payload?.detail || e?.message || "Could not load the tasks.");
        setList((prev) => ({ ...prev, loading: false }));
      });
  }, [projectId, scope, sprintFilter, debouncedQuery]);

  useEffect(() => {
    setList((prev) => ({ ...prev, loading: true }));
    fetchTasks(0, TASK_PAGE, false);
  }, [fetchTasks]);

  // After a change, re-read what is already on screen (not just the first page).
  const reloadTasks = () => fetchTasks(0, Math.min(Math.max(list.rows.length, TASK_PAGE), 200), false);

  const gitLinks = useTaskGitLinks(projectId);
  const load = useCallback(() => {
    apiGet(`/Project/board/${projectId}/`)
      .then((res) => setBoard(res.project))
      .catch((e) => setError(e?.payload?.detail || e?.message || "Could not open this project."));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // The page behind must not scroll while this sits over it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [active, onClose]);

  const toggle = async (task) => {
    await apiPatch(`/Project/tasks/${task.id}/`, { done: !task.is_done });
    load();
    reloadTasks();
    onDirty();
  };
  const remove = async (task) => {
    await apiDelete(`/Project/tasks/${task.id}/`);
    load();
    reloadTasks();
    onDirty();
  };
  const cancelMeeting = async (meetingId) => {
    if (!window.confirm("Cancel this meeting? It's also removed from everyone's calendar.")) return;
    setError("");
    try {
      await apiPost(`/Project/board/${projectId}/meetings/${meetingId}/cancel/`, {});
      load();
    } catch (e) {
      setError(e?.payload?.detail || e?.message || "Could not cancel this meeting.");
    }
  };
  const updateMeeting = async (meetingId, payload) => {
    setError("");
    try {
      await apiPost(`/Project/board/${projectId}/meetings/${meetingId}/update/`, payload);
      load();
    } catch (e) {
      setError(e?.payload?.detail || e?.message || "Could not update this meeting.");
    }
  };

  const counts = list.counts || board?.task_counts || { all: 0, open: 0, mine: 0 };
  const ownerLabels = useMemo(() => ownerLabelsForTeam(board?.team), [board?.team]);

  return (
    <div
      className={"Board-Scrim" + (active ? "" : " Board-Hidden")}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <section className="Board-Sheet" role="dialog" aria-modal={active} aria-label={board?.name || "Project"}>
        <header className="Board-Head">
          <div>
            <h2>{board?.name || "Loading…"}</h2>
          </div>
          <div className="Board-Head-Actions">
            <button type="button" className="Board-Close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </header>

        {error && <div className="error-banner">{error}</div>}

        {board && (
          <div className="Board-Body">
            <SprintStrip sprints={board.sprints} team={board.team} />

            <div className="Board-Toolbar">
              <div className="Board-Filters">
                {[["open", "Open", counts.open], ["mine", "Mine", counts.mine], ["all", "Everything", counts.all]].map(([key, label, n]) => (
                  <button
                    key={key}
                    type="button"
                    className={"Filter-Chip" + (scope === key ? " on" : "")}
                    onClick={() => setScope(key)}
                  >
                    {label}{n > 0 && <span className="Filter-Chip-N">{n}</span>}
                  </button>
                ))}
              </div>
              {board.sprints.length > 0 && (
                <select className="Board-Sprint-Filter" value={sprintFilter} onChange={(e) => setSprintFilter(e.target.value)} aria-label="Filter by sprint">
                  <option value="">All sprints</option>
                  {board.sprints.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  <option value="none">No sprint</option>
                </select>
              )}
              <input
                className="Board-Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tasks or ERP-…"
                aria-label="Search tasks"
              />
              <button type="button" className="Add-Task-Btn compact" onClick={() => setAdding((v) => !v)} aria-expanded={adding}>
                <Plus size={13} /> Task
              </button>
            </div>

            <AddTask
              projectId={projectId}
              sprints={board.sprints}
              team={board.team}
              open={adding}
              onClose={() => setAdding(false)}
              onAdded={() => { load(); reloadTasks(); onDirty(); }}
            />

            <div className="Board-Tasks">
              {list.rows.length
                ? list.rows.map((t) => (
                    <TaskRow key={t.id} task={t} showOwner ownerLabel={ownerLabels[t.owner]} gitLinks={gitLinks[t.id]} onToggle={toggle} onDelete={remove} />
                  ))
                : <p className="Sanity-Empty">{list.loading ? "Loading…" : "Nothing here under this filter."}</p>}
              {list.hasMore && (
                <div className="Board-More">
                  <button type="button" className="Soft-Button Small" onClick={() => fetchTasks(list.rows.length, TASK_PAGE, true)}>
                    Show {Math.min(TASK_PAGE, list.count - list.rows.length)} more
                  </button>
                  <span className="Board-Count">{list.rows.length} of {list.count}</span>
                </div>
              )}
            </div>

            <div className="Board-Meetings">
              {board.meetings.length ? (
                <Fold label="Meetings" count={board.meetings.length} defaultOpen>
                  {board.meetings.map((m) => (
                    <MeetingRow
                      key={m.id}
                      meeting={m}
                      allEmployees={allEmployees}
                      teamWord="team"
                      onUpdate={updateMeeting}
                      onCancel={cancelMeeting}
                    />
                  ))}
                </Fold>
              ) : <p className="Sanity-Empty">No meetings scheduled yet.</p>}
              <ScheduleMeetingForm projectId={projectId} team={board.team} allEmployees={allEmployees} onScheduled={load} />
            </div>

            <div className="Board-Aside">
              <div>
                {board.team.length ? (
                  <Fold label="Team" count={board.team.length}>
                    {board.team.map((m) => (
                      <div key={m.employee_id} className={"Sanity-Team-Row compact" + (m.status === "Exited" ? " exited" : "")}>
                        <TeamIdentity member={m} />
                      </div>
                    ))}
                  </Fold>
                ) : <p className="Sanity-Empty">No one assigned.</p>}
              </div>
              <div>
                {board.repos.length ? (
                  <Fold label="Repositories" count={board.repos.length}>
                    {board.repos.map((r) => (
                      <div key={r.id} className="Exec-Repo">
                        <span className="Exec-Repo-Name">⌥ {r.name}</span>
                        {r.collaborators?.length > 0 && (
                          <span className="Team-Repo-Wrap Repo-Collaborators-Reveal">
                            <span className="Team-Repo-Count">
                              {r.collaborators.length} {r.collaborators.length === 1 ? "collaborator" : "collaborators"}
                            </span>
                            <div className="Repo-Hover-Panel">
                              <p className="Repo-Hover-Title">Who has access</p>
                              {r.collaborators.map((name) => <div key={name} className="Repo-Hover-Item">{name}</div>)}
                            </div>
                          </span>
                        )}
                        <CopyButton value={r.clone_url} label={`Copy ${r.name}'s clone URL`} />
                      </div>
                    ))}
                  </Fold>
                ) : <p className="Sanity-Empty">None linked.</p>}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function ProjectCard({ project, onExpand, onChanged, isOpen, gitLinks }) {
  const toggle = async (task) => {
    await apiPatch(`/Project/tasks/${task.id}/`, { done: !task.is_done });
    onChanged();
  };
  const remove = async (task) => {
    await apiDelete(`/Project/tasks/${task.id}/`);
    onChanged();
  };

  return (
    // "active" carries the same highlight as :hover, applied without a real
    // hover — while this card's board is open, you're still conceptually
    // inside this project's space, even with the mouse over a button inside
    // the board sheet on top of it.
    <div className={"Exec-Project ds-card-hover" + (isOpen ? " active" : "") + (project.is_paused ? " paused" : "")}>
      <div className="Exec-Project-Head">
        <div className="Exec-Project-Title">
          {project.is_paused && (
            <span className="Paused-Badge" title="Paused">
              <Pause size={11} /> Paused
            </span>
          )}
          <h2>{project.name}</h2>
        </div>
        <button type="button" className="Expand-Btn" onClick={() => onExpand(project.id)}>
          <Maximize2 size={13} /> Open whole project
        </button>
      </div>

      <SprintStrip sprints={project.sprints} team={project.team} />

      <div className="Exec-Body">
        {project.tasks.length ? (
          <>
            <p className="Exec-Side-Label">Your tasks ({project.my_open_count} open)</p>
            {project.tasks.map((t) => <TaskRow key={t.id} task={t} gitLinks={gitLinks?.[t.id]} onToggle={toggle} onDelete={remove} />)}
          </>
        ) : (
          <p className="Sanity-Empty">Nothing assigned to you here.</p>
        )}

        <div className="Exec-Card-Foot">
          <AddTask projectId={project.id} sprints={project.sprints} team={project.team} onAdded={onChanged} />
          <span className="Exec-Foot-Meta">
            {/* Meetings first, and only when there are any — presence is the
               signal Bassi actually scans for ("do I see 2 things or 3?"),
               so a zero must not render at all, not show as "0 meetings".
               It also stays at full color whether or not you're hovering the
               card — team and repos are the ones that dim, on purpose: both
               are stable reference facts (team size is tracked in HRMS
               anyway) that don't need attention every glance, so they stay
               muted at all times, hover included. Only Open Whole Project
               brightens on card-hover; this line never does. */}
            {project.meeting_count > 0 && (
              <>
                <span className="Exec-Foot-Highlight">
                  {project.meeting_count} {project.meeting_count === 1 ? "meeting" : "meetings"}
                </span>
                {" · "}
              </>
            )}
            <span className="Exec-Foot-Muted">
              {project.team.length} team · {project.repo_count} {project.repo_count === 1 ? "repo" : "repos"}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

export function ProjectsScreen({ data }) {
  const [projects, setProjects] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  // Every project board ever opened this session, kept mounted (hidden once
  // closed) instead of destroyed — closing one by clicking outside, Escape,
  // or the X must not lose whatever was mid-edit inside it. Cleared only by
  // an actual page refresh, same as any other in-memory state.
  const [openedIds, setOpenedIds] = useState([]);
  const allEmployees = data?.employees || [];
  const myGitLinks = useTaskGitLinks();

  const load = useCallback(() => {
    apiGet("/Project/my-projects/")
      .then((res) => setProjects(res.projects || []))
      .catch((e) => setError(e?.payload?.detail || e?.message || "Could not load your projects."));
  }, []);

  useEffect(() => { load(); }, [load]);

  const expand = (id) => {
    setExpanded(id);
    setOpenedIds((current) => (current.includes(id) ? current : [...current, id]));
  };

  return (
    <section className="Exec-Page">
      <IntegrationHealthBanner />
      <div className="Sanity-Head"><h1>Projects</h1></div>
      <p className="Sanity-Sub">
        Every project you're on — your own tasks and how each sprint is tracking. Open a project to see the whole team's work.
      </p>

      {error && <div className="Sanity-Why critical">{error}</div>}
      {!error && projects === null && <p className="Sanity-Empty">Loading…</p>}
      {!error && projects && !projects.length && (
        <p className="Sanity-Empty">You're not on any project yet. Ask whoever runs the project to add you to the team.</p>
      )}
      {!error && projects && projects.map((p) => (
        <ProjectCard key={p.id} project={p} onExpand={expand} onChanged={load} isOpen={expanded === p.id} gitLinks={myGitLinks} />
      ))}

      {openedIds.map((id) => (
        <ProjectBoard
          key={id}
          projectId={id}
          allEmployees={allEmployees}
          active={expanded === id}
          onClose={() => setExpanded(null)}
          onDirty={load}
        />
      ))}
    </section>
  );
}
